import React from "react";
import { useState, useEffect, useContext } from "react";
import * as styles from "./style.mod.scss";
import GlobalContext from "services/global-context";
import { fetchUpsellProducts } from "data/products";
import SidePanel from "../side-panel";
import translations from "locales";
import CartItem from "./item";
import CartRecommendations from "./recommendations";
import EmptyCart from "./empty-cart";
import DiscountCode from "./discount-code";
import CartHeader from "./header";
import CartFooter from "./footer";
import LoadingCart from "./loading-cart";
import { trackViewCart } from "helpers/analytics";

const Cart = (props) => {
  const {
    cartOpened, toggleCart,
    cart, refreshCart
  } = useContext(GlobalContext);
  const cartItems = cart?.items ? cart.items : [];
  const [loading, setLoading] = useState(true);
  const [cartRefreshing, setCartRefreshing] = useState(true);
  const [products, setProducts] = useState([]);
  const cartLoc = translations.cart;
  const { data, settings } = props;
  const totalQuantity = cart?.totalQuantity;

  let totalCartValue = (+cart?.totalPriceV2?.amount + +cart?.discountedAmount).toFixed(2);

  useEffect(() => {
    if (cartOpened)
      cart?.items && trackViewCart(cart, "shopify");
  }, [cartOpened]);

  const showRecommendations = false;

  cartItems.forEach((item) => {
    const id = btoa(item?.variantId).replace("gid://shopify/ProductVariant/", "");
    let override = data[id];

    override?.cartTitle ? item.title = override.cartTitle : "";
    override?.cartSubtitle ? item.variantOptions[0].value = override.cartSubtitle : "";
    override?.image ? item.image.src = override.image : "";
    override?.alt ? item.image.altText = override.alt : "";
    override?.upsellProductIds ? item.upsellProductIds = override.upsellProductIds : "";
  });

  products?.forEach((item) => {
    let override = data[item.id];

    override?.cartTitle ? item.title = override.upsellTitle : "";
    override?.upsellSubtitle ? item.upsellSubtitle = override.upsellSubtitle : "";
    override?.image ? item.image.src = override.image : "";
    override?.alt ? item.image.altText = override.alt : "";
  });

  const fillCart = async () => {
    await refreshCart();
    setLoading(false);
  };

  useEffect(() => {
    const handleCartUpdate = async () => {
      if (cartOpened) {
        setCartRefreshing(true);
        await fillCart();
        setCartRefreshing(false);
        setLoading(false);
      }
    };

    handleCartUpdate();
  }, [cartOpened]);


  useEffect(() => {
    const processUpsells = async () => {
      /* 
      1. Get variant upsells from sanity for each line item
      2. Fetch Shopify API for all the variant ids 
      3. Loop though the results, filter accordingly, sort and splice the first 3
      4. Add the discount code as custom attribute on the upsell/cross-sell item
      */

      if (cart?.items) {
        const getUpsellIds = Array.from(new Set(cartItems.filter(prod => prod.upsellProductIds).flatMap(prod => prod.upsellProductIds)));

        // console.log("fetching upsells", getUpsellIds, cartItems);
        const fetchUpsells = await fetchUpsellProducts(getUpsellIds);
        setLoading(false);

        const currentUpsells = cartItems?.map(item => item.variantId);
        const filteredUpsells = fetchUpsells?.filter(a => !currentUpsells.includes(a.id));

        const sortBySKU = getUpsellIds.map((e) => e.split(":")[1]);
        let splicedFilters = filteredUpsells.sort((a, b) => sortBySKU.indexOf(a.sku) - sortBySKU.indexOf(b.sku)).splice(0, 3);
        let attachDiscount = getUpsellIds.map((e) => e.split(":")[2]);

        splicedFilters.map((lineItem, i) => {
          lineItem.percOff = attachDiscount[i];
        });

        if (splicedFilters?.length >= 0) {
          setProducts(splicedFilters);
        } else {
          setProducts(fetchUpsells);
        }
      }
    };
    processUpsells();

  }, [cart]);

  const isEmpty = cartItems.length === 0;

  const pdpSettings = props?.productSettings;


  if (loading || (cartRefreshing && isEmpty)) return <LoadingCart />;
  if (isEmpty) return <EmptyCart showRecommendations={showRecommendations} />;

  const freeProducts = settings?.freeProd?.variants;
  const discountCodeLowerCase = settings?.discountCode?.toLowerCase();

  let errorMessage;

  for (const variant of freeProducts || []) {
    const variantDiscountCodeLowerCase = variant?.discountCode.toLowerCase();

    if (variant?._type === "triggeredProductsForFree" && discountCodeLowerCase === variantDiscountCodeLowerCase) {
      const hasMatchingVariant = variant?.triggeredVariants?.some((triggeredVariantId) => {
        return cartItems?.some((cartItem) => btoa(cartItem?.variantId) === triggeredVariantId?.id);
      });

      if (!hasMatchingVariant) {
        errorMessage = variant?.message;
        break;
      }
    } else if (discountCodeLowerCase === variantDiscountCodeLowerCase && cart?.totalPriceV2?.amount < variant?.amountForFreeGift) {
      errorMessage = variant?.message;
      break;
    }
  }

  return (
    <SidePanel
      title={cartLoc.yourCart}
      titleExtra={`(${totalQuantity})`}
      open={cartOpened}
      onClose={toggleCart}
      className={`${styles.cart}
      ${!showRecommendations ? `${styles.reducePanelWidth}` : "small-cart"}`}
      closeIconStyle={styles.closeIcon} >
      <div className={styles.body}>
        <CartHeader settings={settings} discount={cart?.discount?.code} totalQuantity={totalQuantity} totalCartValue={totalCartValue} />
        <div className={styles.cartInfo}>
          <div className={cartItems?.length < 1 ? styles.items : `${styles.items} ${styles.scroll}`}>

            {cartItems?.map((item) => (
              <CartItem item={item} key={item?.key} />
            ))}
            <DiscountCode showDiscountField={settings?.showDiscountField} discountCode={settings?.discountCode} errorMessage={errorMessage} freeProducts={freeProducts} />

          </div>
          <CartFooter pdpSettings={pdpSettings} />
        </div>
      </div>
      <CartRecommendations showRecommendations={showRecommendations} products={products} />
    </SidePanel>
  );
};

export default Cart;